<template>
	<button
		v-if="!routerLinkTo"
		tabindex="0"
		class="bbox-button cursor-pointer"
		:disabled="disabled"
		:class="[paddings, colors, { success, tiny, outline, link, custom, disabled, danger, block }]"
		@click.prevent="(disabled || loading) ? null : onClickOrEnter($event)"
		@keydown.enter.prevent="(disabled || loading) ? null : onClickOrEnter($event)"
	>
		<b-icon v-if="loading" :class="{ 'padding-horizontal-big': link }" spin><icon-refresh /></b-icon>
		<!-- @slot Slot onde conteúdo do botão é inserido -->
		<slot v-else></slot>
	</button>
	<b-router-link
		v-else
		:to="routerLinkTo ? routerLinkTo : ''"
		:disabled="disabled || loading ? true : false"
		>
		<button
			tabindex="0"
			class="bbox-button"
			:class="[paddings, colors, { success, tiny, outline, link, custom, disabled, danger, block }]"
		>
			<b-icon v-if="loading" :class="{ 'padding-horizontal-big': link }" spin><icon-refresh /></b-icon>
			<!-- @slot Slot onde conteúdo do botão é inserido -->
			<slot v-else></slot>
		</button>
	</b-router-link>
</template>

<script>
import BIcon from './Icon.vue';
import BRouterLink from './RouterLink.vue';
import IconRefresh from '../icons/icon-refresh.svg';

export default {
	name: 'b-button',
	components: {
		BIcon,
		BRouterLink,
		IconRefresh
	},
	props: {
		/**
		 * Se true botão tem classes de sucesso
		 */
		success: Boolean,
		/**
		 * Se true botão tem classes de danger
		 */
		danger: Boolean,
		/**
		 * Se true botão tem formato de outline (background claro e borda)
		 */
		outline: Boolean,
		/**
		 * Se true botão não tem classes pré-definidas
		 */
		custom: Boolean,
		/**
		 * Se true botão tem padding reduzido
		 */
		tiny: Boolean,
		/**
		 * Se true botão não tem background, nem borda
		 */
		link: Boolean,
		/**
		 * Se true botão exibe spinner de loading e desabilita click
		 */
		loading: Boolean,
		/**
		 * Se true botão fica desabilitado
		 */
		disabled: Boolean,
		/**
		 * Se true botão tem `display: block`
		 */
		block: Boolean,
		/**
		 * Define a url de redirecionamento para o botao, que passa a funcionar como um router-link
		 */
		routerLinkTo: String,
		/**
		 * Seta a cor do botão, pode ser qualquer cor base disponível no DLS
		 */
		color: {
			type: String,
			default: 'blue'
		}
	},
	computed: {
		paddings() {
			var classNames = 'padding-horizontal-big padding-vertical-normal';
			if (this.tiny === true) {
				classNames = 'padding-horizontal-normal padding-vertical-tiny';
			} else if (this.routerLinkTo && this.link) {
				classNames = 'padding-0';
			} else if (this.link) {
				classNames = '';
			} else if (this.custom) {
				classNames = '';
			}
			return classNames;
		},
		colors() {
			var classNames = '';
			var color = this.color;
			if (this.disabled) {
				if (this.link) {
					classNames = 'ink-lighter-3 active-ink-lighter-2';
				} else if (this.outline) {
					classNames = 'bg-white-darker-3 border border-ink-lighter-3 ink-lighter-3';
				} else {
					classNames = 'bg-ink-lighter-2 white-darker-4 border border-transparent';
				}
			} else {
				if (this.success) color = 'green';
				else if (this.danger) color = 'red';
				if (this.link) {
					if (this.color === 'gray' || this.color === 'grey') {
						classNames = `ink-lighter-1
                        active-ink-base
                        focus-ink-base
                        hover-ink-base
                        border-none`;
					} else if (this.color === 'white') {
						classNames = `white-base
                        active-white-darker-1
                        focus-white-darker-1
                        hover-white-darker-1
                        border-none`;
					} else {
						classNames = `${color}-base
                        active-${color}-darker-2
                        focus-${color}-darker-1
                        hover-${color}-darker-1
                        border-none`;
					}
				} else if (this.outline) {
					classNames = `bg-${color}-lighter-4 border border-${color}-lighter-2 ${color}-base
                    bg-active-${color}-lighter-3 border-active-${color}-base
                    bg-hover-${color}-lighter-3 border-hover-${color}-lighter-1
                    bg-focus-${color}-lighter-3 border-focus-${color}-lighter-1`;
				} else if (this.custom) {
					classNames = ``;
				} else {
					classNames = `bg-${color}-base white-base
                    bg-active-${color}-darker-2
                    bg-hover-${color}-darker-1
                    bg-focus-${color}-darker-1
                    border-none`;
				}
			}
			return classNames;
		}
	},
	methods: {
		onClickOrEnter(event) {
			/**
			 * Evento emitido quando usuário clica ou aperta enter enquanto está focado no componente
			 * @property {event}
			 * @type {click, keydown}
			 */
			this.$emit('clickOrEnter', event);
		}
	},
};
</script>

<style scoped lang="scss">
.bbox-button {
	transition: background 0.25s, box-shadow 0.25s, color 0.25s;
	box-shadow: 0px 2px 2px 0 rgba(0, 0, 0, 0.14);
	display: inline-block;
	border-radius: 5px;
	box-sizing: border-box;
	font-family: 'Inter', sans-serif;
	letter-spacing: 0.012em;
	font-weight: 500;
	&.block {
		display: block;
	}
	&.disabled {
		cursor: not-allowed !important;
		/* background-color: #C5C3CD;
            color: #EFEEF1; */
	}
	&:active {
		box-shadow: none;
	}
	&.custom {
		display: flex;
		align-items: center;
		box-shadow: none;
		&.disabled {
			background: transparent ;
			border: none !important;
		}
	}
	&.link {
		background: transparent;
		box-shadow: none;
		&.disabled {
			background: transparent !important;
			border: none !important;
		}
	}
}
.router-link .bbox-button {
	width: inherit;
}
</style>

<docs>
    Botão padrão do DLS

    As importações dos arquivos abaixo são necessárias para o funcionamento do componente:
    - styling.scss
    - spacing.scss

    ```js
    <b-button>Default</b-button>
    <b-button success>Success</b-button>
    <b-button danger>Danger</b-button>
    <b-button disabled>Disabled</b-button>
    <b-button outline>Outline</b-button>
    <b-button outline tiny>Outline+Tiny</b-button>
    <b-button tiny>Tiny</b-button>
    <b-button tiny color="yellow">Yellow Tiny</b-button>
    <b-button link>Link</b-button>
    <b-button custom>Custom</b-button>

    ```
</docs>
